import {
  AccountRole,
  AccountRoleUpdate,
  AvailableWorkspace,
  EmailUpdateRequest,
  EmailUpdateResponse,
  StaffUser,
  UpdateUserRequest,
  UpdateUserResponse,
  User,
} from '@customTypes/user/User';
import { formatError } from '@utils/FormatError';
import ApiService from './ApiService';

interface UsersWithWorkspacesResponse {
  users: StaffUser[];
  availableWorkspaces: AvailableWorkspace[];
}

// Per-company Settings → Internal controls for the account in view. Most enable
// dark capabilities; daily_groom_enabled is the default-on opt-out exception.
export interface MachineFlags {
  // Percuity MCP access at all (every connector, key and plugin session). On by default;
  // off refuses every MCP call for the account and blocks new connections and keys.
  mcp_access_enabled: boolean;
  alignment_machine_enabled: boolean;
  bet_machine_enabled: boolean;
  mcp_activate_enabled: boolean;
  mcp_code_mode_enabled: boolean;
  // Hosted code mode over MCP (execute_code, on our servers). On by default; separate
  // from the plugin flag below, which is the repo DOWNLOAD and stays off by default.
  mcp_execute_code_enabled: boolean;
  mcp_expert_client_enabled: boolean;
  // Per-account Code Mode opt-in: routes the account in view to the code-execution
  // agent (auto, for its non-internal users). The env master (LEO_CODE_MODE_WRITES)
  // stays the global default; this is the per-account on switch.
  code_mode_enabled: boolean;
  // Per-account Code Mode SIMPLE opt-in: same, but routes to the primitives-only variant.
  code_mode_simple_enabled: boolean;
  // Per-company opt-in for the backend perpetual machine (audit refresh, weekly
  // beat, onboarding flow, market pulse, review sweep). UI-independent — the new
  // Work Board home feed ships to everyone; this flag only gates the server-side
  // machine that keeps the account moving between sessions.
  new_experience_enabled: boolean;
  // Per-company opt-out for the unified nightly groom. Unlike the dark-launch
  // machines above, this defaults ON so every lifecycle-eligible account is
  // groomed unless an internal operator explicitly disables it.
  daily_groom_enabled: boolean;
  // Interactive-recurrence escape hatch (#5995): lifts the weekly scheduling bound
  // (no crons, ≤7d) for LIVE sync-chat turns only. Default OFF; background runs
  // are never exempt. Backed by machine_flags.interactive_recurrence.
  interactive_recurrence_enabled: boolean;
  // Per-company opt-in for the weekly CREATIVE RUN (the Wednesday creative-workshop
  // beat). The env master (LEO_CREATIVE_RUN) ships non-dark; this defaults OFF and is
  // flipped on from Back Office for accounts that want it. Backed by machine_flags.creative_run.
  creative_run_enabled: boolean;
  // The WEEKLY background beats. All default OFF and are enforced server-side;
  // these are the switches that make "an operator turns it on from Back Office" true.
  // audit_sweep: the Monday run that audits every connected platform, writes the
  // strategic review, and posts one Choose card per platform.
  // creative_refresh: the Monday five-beat run that delivers one card of creatives.
  // understanding_sweep: the Sunday pass that reads live wiring and reconciles the
  // integration-audit surfaces against it.
  audit_sweep_enabled: boolean;
  creative_refresh_enabled: boolean;
  understanding_sweep_enabled: boolean;
  // Shows Shopify in this account's Settings → Connections. Early access: default OFF,
  // turned on from Back Office → Connections & Access. Backed by machine_flags.shopify_connection.
  shopify_connection_enabled: boolean;
  // Staff-managed account opt-in. Shows Executive to all account users; default OFF.
  executive_tab_enabled: boolean;
  // Staff-managed: the executive's action-plan cards, their Do it once, and its first v2
  // check-in. Default OFF; backed by machine_flags.executive_v2.
  executive_v2_enabled: boolean;
  exa_research_enabled: boolean;
  apify_research_enabled: boolean;
}

// The per-platform NO SPEND guard for the account in view (Settings → Internal).
// A platform set to true can still be read and written — created paused, edited,
// restructured, paused — but nothing on it can be activated or created live.
export interface NoSpendFlags {
  meta: boolean;
  google: boolean;
  linkedin: boolean;
  tiktok: boolean;
  reddit: boolean;
  bing: boolean;
  x_ads: boolean;
}

class UserService {
  // Retrieve all users
  public async getAllusers(): Promise<StaffUser[]> {
    const { success, data, error } = await ApiService.get(`/users`);

    if (success) {
      return data as StaffUser[];
    } else {
      throw new Error(formatError(error) || 'Failed to fetch users');
    }
  }

  // Retrieve all users with their workspace access
  public async getAllUsersWithWorkspaces(): Promise<UsersWithWorkspacesResponse> {
    const { success, data, error } = await ApiService.get(
      `/users?include=workspaces`,
    );

    if (success) {
      return data as UsersWithWorkspacesResponse;
    } else {
      throw new Error(formatError(error) || 'Failed to fetch users');
    }
  }

  // Update which workspaces a user can access
  public async updateWorkspaceAccess(
    userId: string,
    workspaceIds: string[],
  ): Promise<{ workspaceIds: string[] }> {
    const { success, data, error } = await ApiService.put(
      `/users/${userId}/workspace-access`,
      { workspaceIds },
    );

    if (success) {
      return data as { workspaceIds: string[] };
    } else {
      throw new Error(
        formatError(error) || 'Failed to update workspace access',
      );
    }
  }

  // Set a person's role, label and voice on the account in view. An empty role
  // with no label and the auto voice clears it.
  public async setAccountRole(
    userId: string,
    update: AccountRoleUpdate,
  ): Promise<AccountRole | null> {
    const { success, data, error } = await ApiService.put(
      `/users/${userId}/account-role`,
      update,
    );

    if (success) {
      return (data as { accountRole: AccountRole | null }).accountRole;
    } else {
      throw new Error(formatError(error) || 'Failed to save the role');
    }
  }

  // Set the persistent per-user internal flag for the current account (the
  // masqueraded account under masquerade). Turning it on enables the Internal tab
  // for that account and persists across sessions.
  public async setInternalAccess(
    isInternal: boolean,
  ): Promise<{ is_internal: boolean }> {
    const { success, data, error } = await ApiService.put(
      `/users/me/internal-access`,
      { is_internal: isInternal },
    );

    if (success) {
      return data as { is_internal: boolean };
    } else {
      throw new Error(formatError(error) || 'Failed to update internal access');
    }
  }

  // Set the per-user domain-expert flag (response-annotation access) for the current
  // account (the masqueraded account under masquerade). Staff only.
  public async setDomainExpert(
    isDomainExpert: boolean,
  ): Promise<{ is_domain_expert: boolean }> {
    const { success, data, error } = await ApiService.put(
      `/users/me/domain-expert`,
      { is_domain_expert: isDomainExpert },
    );

    if (success) {
      return data as { is_domain_expert: boolean };
    } else {
      throw new Error(formatError(error) || 'Failed to update domain expert access');
    }
  }

  // Read the per-company lifecycle-machine override flags for the current account
  // (Settings → Internal). Company-scoped, so this reflects the account in view.
  public async getMachineFlags(): Promise<MachineFlags> {
    const { success, data, error } = await ApiService.get(
      `/users/me/machine-flags`,
    );

    if (success) {
      return data as MachineFlags;
    } else {
      throw new Error(formatError(error) || 'Failed to read machine flags');
    }
  }

  // Set one or more per-company machine controls for the current account. Returns the
  // full updated flag set.
  public async setMachineFlags(
    flags: Partial<MachineFlags>,
  ): Promise<MachineFlags> {
    const { success, data, error } = await ApiService.put(
      `/users/me/machine-flags`,
      flags,
    );

    if (success) {
      return data as MachineFlags;
    } else {
      throw new Error(formatError(error) || 'Failed to update machine flags');
    }
  }

  // Read the per-platform NO SPEND guard for the account in view (Settings → Internal).
  public async getNoSpendFlags(): Promise<NoSpendFlags> {
    const { success, data, error } = await ApiService.get(`/users/me/no-spend`);

    if (success) {
      return data as NoSpendFlags;
    } else {
      throw new Error(formatError(error) || 'Failed to read no-spend settings');
    }
  }

  // Set the guard for one or more platforms. Returns the full updated set.
  public async setNoSpendFlags(
    flags: Partial<NoSpendFlags>,
  ): Promise<NoSpendFlags> {
    const { success, data, error } = await ApiService.put(
      `/users/me/no-spend`,
      flags,
    );

    if (success) {
      return data as NoSpendFlags;
    } else {
      throw new Error(formatError(error) || 'Failed to update no-spend settings');
    }
  }

  // Retrieve all users. Always operator-scoped: this staff-only picker lists
  // who to masquerade INTO, so it must never carry an active masquerade header
  // (which would 403 the operator against the customer's profile).
  public async getVerifiedUsers(): Promise<StaffUser[]> {
    const { success, data, error } = await ApiService.get(
      `/users/verified`,
      undefined,
      { skipMasquerade: true },
    );

    if (success) {
      return data as StaffUser[];
    } else {
      throw new Error(formatError(error) || 'Failed to fetch users');
    }
  }

  // Update Current User
  public async updateCurrentUser(
    requestPayload: UpdateUserRequest,
  ): Promise<User> {
    const { success, data, error } = await ApiService.putForm<
      UpdateUserRequest,
      UpdateUserResponse
    >(`/users/me`, requestPayload);

    if (success) {
      return data as any;
    } else {
      throw new Error(formatError(error) || 'Failed to update user.');
    }
  }

  //Delete Current User
  public async deleteCurrentUser(): Promise<void> {
    const response = await ApiService.delete(`/users/me`);
    if (!response.success) {
      throw new Error(response.error || 'Failed to delete user');
    }
  }

  // Update User by Id
  public async update(
    id: string,
    requestPayload: UpdateUserRequest,
  ): Promise<StaffUser> {
    const { success, data, error } = await ApiService.put<
      UpdateUserRequest,
      UpdateUserResponse
    >(`/users/${id}`, requestPayload);

    if (success) {
      return data as StaffUser;
    } else {
      throw new Error(formatError(error) || 'Failed to update user');
    }
  }

  // Delete User by Id
  public async delete(id: string): Promise<void> {
    const response = await ApiService.delete(`/users/${id}`);
    if (!response.success) {
      throw new Error(response.error || 'Failed to update user');
    }
  }

  // Update Email Notification
  public async updateEmailNotification(
    requestPayload: EmailUpdateRequest,
  ): Promise<EmailUpdateResponse> {
    const { success, data, error } = await ApiService.put<
      EmailUpdateRequest,
      EmailUpdateResponse
    >(`/users/me/settings`, requestPayload);
    if (success) {
      return data as any;
    } else {
      throw new Error(
        formatError(error) || 'Failed to update email notification.',
      );
    }
  }

  // Persist the user's canonical IANA timezone (captured from the browser) so
  // Leo's date/day and scheduling anchor to their wall clock (#4185/#4186).
  public async updateTimezone(timezone: string): Promise<void> {
    const { success, error } = await ApiService.put<
      { timezone: string },
      unknown
    >(`/users/me/settings`, { timezone });
    if (!success) {
      throw new Error(formatError(error) || 'Failed to update timezone.');
    }
  }
}

export default new UserService();
